import { useTranslation } from "react-i18next";
import { ChevronDown, Eye } from "@digital-twins/design-system/icons";
import { SocialTopicPostItem } from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { formatSocialDate, SOCIAL_PLATFORM_LABELS } from "./socialFormat";

interface SocialHiddenPostsProps {
  posts: Array<SocialTopicPostItem>;
  /** The Topic's name, for "Show in <Topic>". */
  topicName: string;
  /** The post an unhide is in flight for; its button is disabled. */
  busyPostToken: string | null;
  onOpenPost: (post: SocialTopicPostItem) => void;
  onUnhide: (post: SocialTopicPostItem) => void;
  /** True while the server has more hidden posts than `posts`: no count, a Show more. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onShowMore?: () => void;
}

/**
 * "Hidden posts · 3" at the foot of the Topic view, collapsed by default
 * (no count while more pages are left, so it is never short):
 * every post a manager hid, each with "Show in <Topic>". Hidden posts stay
 * out of the themes, filters and counts until shown again.
 */
export function SocialHiddenPosts(props: SocialHiddenPostsProps) {
  const {
    posts,
    topicName,
    busyPostToken,
    onOpenPost,
    onUnhide,
    hasMore = false,
    isLoadingMore = false,
    onShowMore,
  } = props;

  const { t, i18n } = useTranslation("app");

  const testId = "social-universe-hidden";

  return (
    <details className="group border-t border-border pt-4" data-testid={testId}>
      <summary
        className="flex cursor-pointer list-none items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden"
        data-testid={`${testId}-toggle`}
      >
        <ChevronDown
          className="h-3.5 w-3.5 -rotate-90 transition-transform group-open:rotate-0"
          aria-hidden="true"
        />
        {hasMore
          ? t("socialListening.topicView.hiddenPostsUncounted")
          : t("socialListening.topicView.hiddenPosts", { count: posts.length })}
      </summary>
      <ul
        className="mt-2 divide-y divide-border"
        data-testid={`${testId}-list`}
      >
        {posts.map((post, index) => {
          const author = post.authorHandle
            ? `@${post.authorHandle}`
            : t("socialListening.detail.unknownAuthor");
          const date = formatSocialDate(
            post.publishedAt ?? post.collectedAt,
            i18n.language,
          );
          return (
            <li
              key={post.token}
              className="flex items-center gap-3 py-2.5 text-[13px]"
              data-testid={`${testId}-post-${index}`}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t("socialListening.topicView.tileAria", {
                  author,
                  platform: SOCIAL_PLATFORM_LABELS[post.platform],
                })}
                data-testid={`${testId}-open-${post.token}`}
                onClick={() => onOpenPost(post)}
              >
                <SocialPlatformIcon
                  platform={post.platform}
                  className="h-3.5 w-3.5 shrink-0"
                />
                <span className="shrink-0 text-foreground">{author}</span>
                <Text
                  as="span"
                  size="sm"
                  color="muted"
                  className="min-w-0 truncate text-[13px]"
                >
                  {[date, post.text].filter(Boolean).join(" · ")}
                </Text>
              </button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                shape="pill"
                className="h-8 shrink-0 px-3 text-[12px]"
                disabled={busyPostToken === post.token}
                data-testid={`${testId}-unhide-${post.token}`}
                onClick={() => onUnhide(post)}
              >
                <Eye className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {t("socialListening.topicPost.unhide", { name: topicName })}
              </Button>
            </li>
          );
        })}
      </ul>
      {hasMore && onShowMore ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 h-8 px-2 text-[12px]"
          disabled={isLoadingMore}
          data-testid={`${testId}-more`}
          onClick={onShowMore}
        >
          {t("socialListening.topicView.hiddenPostsMore")}
        </Button>
      ) : null}
    </details>
  );
}
